import { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, limit, startAfter, DocumentSnapshot, getCountFromServer } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { AcademieReport } from '../../types/academieReport';
import { Plus, X, Eye, BookOpen, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';
import { ReportFilterBar, ReportFilters, defaultFilters, isFiltersActive, applyFilters } from '../ui/ReportFilterBar';
import AcademieForm from './AcademieForm';
import { AcademieReportPreview } from './AcademieReportPreview';
import { useAuth } from '../../contexts/AuthContext';
import { canDeleteReport } from '../../utils/permissions';
import { deleteDoc, doc } from 'firebase/firestore';
import { toast } from 'sonner';
import { useConfirm } from '../../contexts/ConfirmContext';
import AcademieTrendChart from './AcademieTrendChart';

export default function AcademieReportsList() {
  const confirm = useConfirm();
  const { user } = useAuth();
  const [reports, setReports] = useState<AcademieReport[]>([]);
  const [allReports, setAllReports] = useState<AcademieReport[]>([]);
  const [filters, setFilters] = useState<ReportFilters>(defaultFilters());
  const [showForm, setShowForm] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AcademieReport | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastDoc, setLastDoc] = useState<DocumentSnapshot | null>(null);
  const [firstDoc, setFirstDoc] = useState<DocumentSnapshot | null>(null);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const [totalReportsCount, setTotalReportsCount] = useState(0);
  const [pageStartCursors, setPageStartCursors] = useState<Map<number, DocumentSnapshot>>(new Map());
  const reportsPerPage = 10;

  const fetchReports = async (page = 1, direction: 'next' | 'prev' | 'direct' = 'direct', cursor?: DocumentSnapshot | null) => {
    try {
      setIsLoading(true);
      const reportsRef = collection(db, 'academieReports');
      
      // Charger le nombre total de rapports (seulement au premier chargement)
      if (page === 1 && direction === 'direct') {
        const countSnapshot = await getCountFromServer(query(reportsRef));
        setTotalReportsCount(countSnapshot.data().count);
        
        // Charger tous les rapports pour les statistiques et la recherche
        const allReportsQuery = query(reportsRef, orderBy('date', 'desc'));
        const allSnapshot = await getDocs(allReportsQuery);
        const allReportsData = allSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as AcademieReport[];
        setAllReports(allReportsData);
      }
      
      let q;
      
      if (direction === 'next' && cursor) {
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'prev' && cursor) {
        q = query(reportsRef, orderBy('date', 'asc'), startAfter(cursor), limit(reportsPerPage + 1));
      } else if (direction === 'direct' && page > 1 && pageStartCursors.has(page)) {
        const startCursor = pageStartCursors.get(page)!;
        q = query(reportsRef, orderBy('date', 'desc'), startAfter(startCursor), limit(reportsPerPage + 1));
      } else {
        q = query(reportsRef, orderBy('date', 'desc'), limit(reportsPerPage + 1));
      }
      
      const querySnapshot = await getDocs(q);
      let reportsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as AcademieReport[];
      
      if (direction === 'prev') {
        reportsData = reportsData.reverse();
      }
      
      const hasMore = reportsData.length > reportsPerPage;
      if (hasMore) {
        reportsData.pop();
      }
      
      setReports(reportsData);
      setHasNextPage(hasMore);
      setHasPrevPage(page > 1);
      setCurrentPage(page);
      
      if (reportsData.length > 0) {
        const newFirstDoc = querySnapshot.docs[0];
        const newLastDoc = querySnapshot.docs[reportsData.length - 1];
        
        setFirstDoc(newFirstDoc);
        setLastDoc(newLastDoc);
        
        if (!pageStartCursors.has(page)) {
          setPageStartCursors(prev => new Map(prev).set(page, newFirstDoc));
        }
      }
    } catch (error) {
      console.error('Error fetching Academie reports:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const getTotalPages = () => Math.ceil(totalReportsCount / reportsPerPage);

  const goToNextPage = () => {
    if (hasNextPage && lastDoc) {
      fetchReports(currentPage + 1, 'next', lastDoc);
    }
  };

  const goToPrevPage = () => {
    if (hasPrevPage && firstDoc) {
      fetchReports(currentPage - 1, 'prev', firstDoc);
    }
  };

  const goToPage = (pageNumber: number) => {
    if (pageNumber === 1) {
      setPageStartCursors(new Map());
      fetchReports(1, 'direct');
    } else if (pageStartCursors.has(pageNumber)) {
      fetchReports(pageNumber, 'direct');
    } else {
      goToPage(1);
    }
  };

  const handleNewReportSubmit = async () => {
    setShowForm(false);
    setCurrentPage(1);
    setPageStartCursors(new Map());
    fetchReports(1, 'direct');
  };

  const handleDelete = async (reportId: string) => {
    const ok = await confirm({ title: 'Supprimer le rapport', description: 'Êtes-vous sûr de vouloir supprimer ce rapport Académie ?', confirmLabel: 'Supprimer', cancelLabel: 'Annuler', variant: 'danger' });
    if (!ok) return;

    try {
      await deleteDoc(doc(db, 'academieReports', reportId));
      fetchReports(currentPage, 'direct', currentPage > 1 ? pageStartCursors.get(currentPage) : null);
    } catch (error) {
      console.error('Error deleting Academie report:', error);
      toast.error('Erreur lors de la suppression du rapport');
    }
  };

  const filtersActive = isFiltersActive(filters);
  const filteredReports = filtersActive
    ? applyFilters(allReports, filters, r => r.date, r => [new Date(r.date).toLocaleDateString('fr-FR'), r.className, r.moderator, r.courseOfTheDay])
    : reports;

  const totalPages = getTotalPages();


  return (

    <>
      {/* ===== MOBILE VIEW (< md) ===== */}
      <div className="md:hidden">
        <div className="min-h-screen bg-gray-50">
      {showForm && (
        <AcademieForm onSubmit={handleNewReportSubmit} onCancel={() => setShowForm(false)} />
      )}
      {selectedReport && !showForm && (
        <AcademieReportPreview report={selectedReport} onClose={() => setSelectedReport(null)} />
      )}
      {!showForm && !selectedReport && (
        <>
          <div className="bg-vh-green text-white px-4 pt-10 pb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight">Académie</h1>
                  <p className="text-white/70 text-sm">Gestion des rapports</p>
                </div>
              </div>
              <button onClick={() => setShowForm(true)} className="w-12 h-12 bg-vh-gold rounded-full flex items-center justify-center shadow-lg active:scale-95 transition-transform">
                <Plus className="w-6 h-6 text-white" />
              </button>
            </div>
            <div className="bg-white/10 rounded-xl p-3 flex items-center justify-center gap-2">
              <span className="text-2xl font-bold">{filteredReports.length}</span>
              <span className="text-white/70 text-sm">rapport(s) affichés</span>
            </div>
          </div>
          <div className="sticky top-0 z-10 bg-white shadow-sm px-4 py-2">
            <ReportFilterBar filters={filters} onChange={setFilters} />
          </div>
          {isLoading && <div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-vh-green" /></div>}
          {!isLoading && (
            <div className="px-4 py-4 space-y-3">
              {filteredReports.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-gray-400">
                  <BookOpen className="w-12 h-12 mb-3 text-gray-300" />
                  <p className="text-base font-medium">Aucun rapport trouvé</p>
                  <p className="text-sm mt-1">Appuyez sur + pour créer un rapport</p>
                </div>
              ) : (
                filteredReports.map(report => (
                  <div key={report.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="flex items-stretch">
                      <div className="w-14 bg-vh-green flex flex-col items-center justify-center py-3 flex-shrink-0">
                        <span className="text-white font-bold text-xl leading-none">
                          {report.date?.toDate ? report.date.toDate().getDate() : new Date(report.date ?? 0).getDate()}
                        </span>
                        <span className="text-white/80 text-xs mt-0.5">
                          {report.date?.toDate ? report.date.toDate().toLocaleDateString('fr-FR', { month: 'short' }) : new Date(report.date ?? 0).toLocaleDateString('fr-FR', { month: 'short' })}
                        </span>
                      </div>
                      <div className="flex-1 px-3 py-2.5 min-w-0">
                        <p className="font-semibold text-gray-800 text-sm truncate">{report.className}</p>
                        <p className="text-gray-500 text-xs mt-0.5 truncate">{report.courseOfTheDay}</p>
                        {report.moderator && <p className="text-gray-400 text-xs mt-0.5">Modérateur: {report.moderator}</p>}
                        <div className="flex items-center gap-2 mt-1.5">
                          <span className="text-xs bg-vh-green/10 text-vh-green px-2 py-0.5 rounded-full font-medium">
                            {report.presentStudents}/{report.actualStudents} présents
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col border-l border-gray-100 flex-shrink-0">
                        <button onClick={() => setSelectedReport(report)} className="flex-1 flex items-center justify-center px-3 text-blue-500 hover:bg-blue-50 transition-colors">
                          <Eye className="w-4 h-4" />
                        </button>
                        {canDeleteReport(report, user) && (
                          <>
                            <div className="border-t border-gray-100" />
                            <button onClick={() => handleDelete(report.id)} className="flex-1 flex items-center justify-center px-3 text-red-400 hover:bg-red-50 transition-colors">
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
          {(hasPrevPage || hasNextPage) && (
            <div className="flex items-center justify-between px-4 pb-6 pt-2">
              <button onClick={goToPrevPage} disabled={!hasPrevPage} className={hasPrevPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                <ChevronLeft className="w-4 h-4" /> Précédent
              </button>
              <span className="text-sm text-gray-500 font-medium">Page {currentPage}</span>
              <button onClick={goToNextPage} disabled={!hasNextPage} className={hasNextPage ? 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-vh-green text-white' : 'flex items-center gap-1.5 px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 text-gray-400 cursor-not-allowed'}>
                Suivant <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </>
      )}
    </div>
      </div>

      {/* ===== DESKTOP VIEW (>= md) ===== */}
      <div className="hidden md:block">
        <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Rapports Académie</h1>
        <button
          onClick={() => setShowForm(!showForm)}
           className={`inline-flex items-center px-4 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2 transition-colors ${
             showForm 
               ? 'bg-gray-600 text-white hover:bg-gray-700' 
               : 'bg-[#00665C] text-white hover:bg-[#004d45]'
           }`}
        >
          {showForm ? (
            <>
              <X size={20} className="mr-2" />
              Fermer le formulaire
            </>
          ) : (
            <>
              <Plus size={20} className="mr-2" />
              Nouveau rapport Académie
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 sm:p-6">
          <AcademieForm onSubmit={handleNewReportSubmit} onCancel={() => setShowForm(false)} />
        </div>
      )}

      {/* Graphique d'évolution de la présence */}
      <AcademieTrendChart reports={allReports} />
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex flex-col gap-4 mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Liste des rapports</h2>
            <p className="text-sm text-gray-600 mt-1">
              {totalReportsCount} rapport{totalReportsCount !== 1 ? 's' : ''} au total
            </p>
          </div>
          <div>
            <ReportFilterBar
              filters={filters}
              onChange={setFilters}
              onReset={() => setFilters(defaultFilters())}
              placeholder="Rechercher par date, classe ou modérateur…"
              resultCount={filteredReports.length}
              totalCount={allReports.length || totalReportsCount}
            />
          </div>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-4 border-[#00665C]/25 border-t-[#00665C]"></div>
          </div>
        ) : filteredReports.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <BookOpen size={48} className="mb-4" />
            <p className="text-lg font-medium">Aucun rapport Académie trouvé</p>
            <p className="text-sm">Créez votre premier rapport Académie</p>
          </div>
        ) : (
          <div className="overflow-hidden">
            {/* Mobile view: Cards */}
            <div className="lg:hidden space-y-4">
              {filteredReports.map((report) => (
                <div key={report.id} className="bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="text-sm text-gray-500">
                        {new Date(report.date).toLocaleDateString('fr-FR')}
                      </div>
                      <div className="font-medium">{report.className} - {report.courseOfTheDay}</div>
                    </div>
                    <button
                      onClick={() => setSelectedReport(report)}
                      className="text-[#00665C] hover:text-[#004d45] transition-colors"
                      title="Prévisualiser le rapport"
                    >
                      <Eye size={18} />
                    </button>
                    {canDeleteReport(report, user) && (
                      <button
                        onClick={() => handleDelete(report.id!)}
                        className="text-red-600 hover:text-red-800 transition-colors"
                        title="Supprimer le rapport"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                  <div className="space-y-1 text-sm">
                    <div>Modérateur: {report.moderator}</div>
                    <div>Présents: {report.presentStudents} / {report.actualStudents}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop view: Table */}
            <div className="hidden lg:block">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Classe</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Modérateur</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cours du jour</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Présents</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {new Date(report.date).toLocaleDateString('fr-FR')}
                      </td>
                      <td className="px-6 py-4">{report.className}</td>
                      <td className="px-6 py-4">{report.moderator}</td>
                      <td className="px-6 py-4">{report.courseOfTheDay}</td>
                      <td className="px-6 py-4">{report.presentStudents} / {report.actualStudents}</td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="text-[#00665C] hover:text-[#004d45] transition-colors"
                          title="Prévisualiser le rapport"
                        >
                          <Eye size={18} />
                        </button>
                        {canDeleteReport(report, user) && (
                          <button
                            onClick={() => handleDelete(report.id!)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                            title="Supprimer le rapport"
                          >
                            <Trash2 size={18} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {!filtersActive && filteredReports.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4 mt-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Page {currentPage} sur {totalPages} • {totalReportsCount} rapports au total
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={goToPrevPage}
                disabled={!hasPrevPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasPrevPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Précédent
              </button>
              
              <div className="flex items-center space-x-1">
                {(() => {
                  const pageNumbers: (number | string)[] = [];
                  
                  // Toujours inclure la page 1
                  if (totalPages > 0) {
                    pageNumbers.push(1);
                  }
                  
                  // Ajouter des ellipses si nécessaire avant la page actuelle
                  if (currentPage > 4) {
                    pageNumbers.push('...');
                  }
                  
                  // Ajouter les pages autour de la page actuelle
                  const startPage = Math.max(2, currentPage - 1);
                  const endPage = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = startPage; i <= endPage; i++) {
                    if (!pageNumbers.includes(i)) {
                      pageNumbers.push(i);
                    }
                  }
                  
                  // Ajouter des ellipses si nécessaire après la page actuelle
                  if (currentPage < totalPages - 3) {
                    pageNumbers.push('...');
                  }
                  
                  // Toujours inclure la dernière page si elle n'est pas déjà incluse
                  if (totalPages > 1 && !pageNumbers.includes(totalPages)) {
                    pageNumbers.push(totalPages);
                  }
                  
                  return pageNumbers.map((pageNum, index) => {
                    if (pageNum === '...') {
                      return (
                        <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                          ...
                        </span>
                      );
                    }
                    
                    return (
                      <button
                        key={pageNum}
                        onClick={() => goToPage(pageNum as number)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          pageNum === currentPage
                            ? 'bg-[#00665C] text-white'
                            : 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  });
                })()}
              </div>
              
              <button
                onClick={goToNextPage}
                disabled={!hasNextPage}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  hasNextPage
                    ? 'text-[#00665C] bg-[#00665C]/8 hover:bg-[#00665C]/15'
                    : 'text-gray-400 bg-gray-100 cursor-not-allowed'
                }`}
              >
                Suivant
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {selectedReport && (
        <AcademieReportPreview
          report={selectedReport}
          onClose={() => setSelectedReport(null)}
        />
      )}
    </div>
      </div>
    </>
  );
}
